// Inkbird IBS-TH1 / IBS-TH2 adapter.
// The probes broadcast advertising data without pairing; the temperature/humidity
// values live in the manufacturer_data block.
//
// Reverse-engineered layout reported by the open-source community
// (ThePBone/inkbird-ibsth1mini, Home Assistant BTHome integrations):
//   byte 0..1  : temperature   (int16 LE, value / 100)
//   byte 2..3  : humidity      (uint16 LE, value / 100)  — only populated on TH1/TH2 (not IBS-TH1-mini)
//   byte 4     : probe type    (0x00 external, 0x01 internal)
//   byte 5     : unused / CRC-ish
//   byte 6     : battery percent (uint8, 0-100)
//   byte 7..8  : CRC / magic   — varies
//
// TODO(verify): Confirm byte positions on a real IBS-TH2-plus device; some firmware
// revisions shift humidity/battery by 1 byte. Several community decoders also
// look for the company id 0x0001 (a well-known "wildcard" Bluetooth SIG value that
// Inkbird re-uses) — we accept both 0x0001 and the LocalName "sps".

import type { ScanResult } from '@capacitor-community/bluetooth-le';
import {
  GenericBleProbeAdapter,
  type AdvertisingReading,
} from './generic-ble.js';
import type { ProbeVendor } from '@trackeat/shared';

const INKBIRD_COMPANY_IDS = [0x0001, 0x004c];
const INKBIRD_LOCAL_NAMES = ['sps', 'tps', 'xbs'];

export class InkbirdAdapter extends GenericBleProbeAdapter {
  readonly vendor: ProbeVendor = 'inkbird';

  protected parseAdvertisement(result: ScanResult): AdvertisingReading | null {
    const name = (result.localName ?? result.device?.name ?? '').toLowerCase();
    const matchesName = INKBIRD_LOCAL_NAMES.some((n) => name.startsWith(n));
    const bytes = this.pickManufacturerBytes(result);
    if (!matchesName && !bytes) return null;
    if (!bytes || bytes.length < 7) return null;

    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const tempRaw = view.getInt16(0, true);
    const humRaw = view.getUint16(2, true);
    const battery = view.getUint8(6);

    // Sanity: Inkbird operational range is roughly -40..+85 C. Reject outliers.
    const temp = tempRaw / 100;
    if (temp < -50 || temp > 100) return null;
    const hum = humRaw === 0xffff ? null : humRaw / 100;

    const deviceId = result.device?.deviceId ?? result.device?.name ?? '';
    if (!deviceId) return null;

    return {
      reading: {
        device_id: deviceId,
        temperature_c: temp,
        humidity_pct: hum,
        battery_pct: battery > 100 ? null : battery,
        received_at: this.nowIso(),
      },
      probe: {
        device_id: deviceId,
        name: result.device?.name ?? 'Inkbird',
        vendor: 'inkbird',
        model_hint: matchesName ? name : null,
        rssi: result.rssi ?? null,
        last_seen: this.nowIso(),
      },
    };
  }

  private pickManufacturerBytes(result: ScanResult): Uint8Array | null {
    for (const cid of INKBIRD_COMPANY_IDS) {
      const bytes = this.manufacturerBytes(result, cid);
      if (bytes) return bytes;
    }
    return null;
  }
}
